import React, { useState } from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { useWalletAuth } from '../context/WalletAuthContext';
import NotificationBell from '../components/NotificationBell';
import {
  LayoutDashboard, Users, FileSpreadsheet, Store, ArrowLeftRight,
  Wallet, LogOut, Menu, Heart, CreditCard, BarChart3, Search, FileText, Settings, Upload, Calendar
} from 'lucide-react';

const NAV = [
  { path: '/company-admin', icon: LayoutDashboard, label: 'Dashboard', end: true },
  { path: '/company-admin/employees', icon: Users, label: 'Employees' },
  { path: '/company-admin/bulk-upload', icon: Upload, label: 'Bulk Upload' },
  { path: '/company-admin/payroll', icon: FileSpreadsheet, label: 'Payroll' },
  { path: '/company-admin/payroll-schedules', icon: Calendar, label: 'Payroll Schedules' },
  { path: '/company-admin/wallets', icon: Wallet, label: 'Wallets' },
  { path: '/company-admin/fund-wallets', icon: CreditCard, label: 'Fund Wallets' },
  { path: '/company-admin/banking', icon: CreditCard, label: 'Banking' },
  { path: '/company-admin/flex-benefits', icon: Heart, label: 'Flex Benefits' },
  { path: '/company-admin/transactions', icon: ArrowLeftRight, label: 'Transactions' },
  { divider: true, label: 'Benefits & Vendors' },
  { path: '/company-admin/vendors', icon: Store, label: 'Vendors' },
  { path: '/company-admin/benefits', icon: Heart, label: 'Benefits' },
  { path: '/company-admin/payouts', icon: CreditCard, label: 'Payouts' },
  { path: '/company-admin/reports', icon: BarChart3, label: 'Reports' },
  { divider: true, label: 'Recruitment' },
  { path: '/company-admin/offer-letters', icon: FileText, label: 'Offer Letters' },
  { divider: true, label: 'Settings' },
  { path: '/company-admin/settings', icon: Settings, label: 'Company Settings' },
];

export default function CompanyAdminLayout() {
  const { user, logout } = useWalletAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const handleLogout = () => { logout(); };

  return (
    <div className="min-h-screen bg-gray-50" data-testid="company-admin-layout">
      {sidebarOpen && <div className="fixed inset-0 bg-black/20 z-40 lg:hidden" onClick={() => setSidebarOpen(false)} />}
      <aside className={`fixed top-0 left-0 bottom-0 w-60 bg-white border-r border-gray-100 z-50 transform transition-transform lg:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className="flex flex-col h-full">
          <div className="p-5 border-b border-gray-100">
            <img src="/pebbles_logo.svg" alt="Pebbles" className="h-8" />
          </div>
          <nav className="flex-1 py-4 px-3 space-y-0.5 overflow-y-auto">
            {NAV.map((item, i) => item.divider ? (
              <p key={i} className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider px-3 pt-5 pb-2">{item.label}</p>
            ) : (
              <NavLink key={item.path} to={item.path} end={item.end}
                onClick={() => setSidebarOpen(false)}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-all ${
                    isActive ? 'bg-pink-50 text-pink-600 font-medium' : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                  }`
                }>
                <item.icon className="w-[18px] h-[18px]" />
                <span>{item.label}</span>
              </NavLink>
            ))}
          </nav>
          <div className="p-3 border-t border-gray-100">
            <button onClick={handleLogout} className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-gray-500 hover:bg-red-50 hover:text-red-600 w-full transition-colors" data-testid="ca-logout-btn">
              <LogOut className="w-[18px] h-[18px]" /> Sign Out
            </button>
          </div>
        </div>
      </aside>
      <div className="lg:ml-60">
        <header className="sticky top-0 z-30 bg-white/80 backdrop-blur-md border-b border-gray-100">
          <div className="flex items-center justify-between h-14 px-4 lg:px-6">
            <div className="flex items-center gap-3">
              <button className="lg:hidden p-2 rounded-lg hover:bg-gray-50" onClick={() => setSidebarOpen(true)}>
                <Menu className="w-5 h-5 text-gray-600" />
              </button>
              <div className="hidden sm:flex items-center gap-2 bg-gray-50 rounded-lg px-3 py-2 text-sm text-gray-400 w-64">
                <Search className="w-4 h-4" /> Search...
              </div>
            </div>
            <div className="flex items-center gap-3">
              <NotificationBell />
              <div className="flex items-center gap-2 pl-3 border-l border-gray-100">
                <div className="w-8 h-8 rounded-full bg-pink-100 text-pink-600 flex items-center justify-center text-xs font-bold">
                  {user?.first_name?.[0]}{user?.last_name?.[0]}
                </div>
                <span className="text-sm font-medium text-gray-700 hidden sm:block">{user?.first_name}</span>
              </div>
            </div>
          </div>
        </header>
        <main className="p-4 lg:p-6"><Outlet /></main>
      </div>
    </div>
  );
}
